/**
 * A composition is the editable source of a document: an ordered list of
 * block instances with their inputs. It is data, not code — which is what
 * makes a per-client document variant a stored row rather than a deploy.
 *
 * Compiling a composition runs every block's compiler once and throws the
 * conditional logic away, keeping only what it decided. That output is the
 * thing worth persisting: it survives the block being rewritten, and it
 * survives the block being deleted.
 */
import { IR_SCHEMA, IR_VERSION } from '../ir/nodes.js';
import { compileBlock } from '../block/compile.js';
import { evaluateCondition } from '../block/slots.js';
import { assembleDocument } from '../assemble/assemble.js';
export const COMPOSITION_SCHEMA = 'regta.document.composition';
export const COMPOSITION_VERSION = 1;
export function compileComposition(composition, registry, context, options = {}) {
    const diagnostics = [];
    const declaredParts = composition.parts?.length
        ? composition.parts
        : [{ key: 'main', title: composition.title }];
    const partBuckets = new Map(declaredParts.map((p) => [p.key, { title: p.title, body: [], letterhead: [], footer: [] }]));
    for (const slot of composition.meta?.partySlots ?? []) {
        if (!slot.required || context.mode === 'blank')
            continue;
        if (!context.parties.slot(slot.role, slot.ordinal ?? 0)) {
            diagnostics.push({
                severity: 'error',
                code: 'missing_required_party',
                message: `Dokumentet kräver parten "${slot.label}" (${slot.role}/${slot.ordinal ?? 0}).`,
            });
        }
    }
    const attachments = [...(composition.attachments ?? [])];
    const manifest = [];
    const seenInstances = new Set();
    for (const instance of composition.blocks) {
        if (seenInstances.has(instance.instanceId)) {
            diagnostics.push({
                severity: 'error',
                code: 'duplicate_instance_id',
                message: `Block instance id "${instance.instanceId}" is used more than once.`,
                instanceId: instance.instanceId,
            });
            continue;
        }
        seenInstances.add(instance.instanceId);
        const definition = registry.resolve(instance.blockRef);
        if (!definition) {
            diagnostics.push({
                severity: 'error',
                code: 'unknown_block',
                message: `No registered block "${instance.blockRef}".`,
                instanceId: instance.instanceId,
                blockRef: instance.blockRef,
            });
            continue;
        }
        manifest.push({
            instanceId: instance.instanceId,
            blockKey: definition.blockKey,
            blockVersion: definition.version,
            ...(definition.minor != null ? { blockMinor: definition.minor } : {}),
            plane: definition.plane,
        });
        const scope = { ...(composition.values ?? {}), ...instance.input };
        if (context.mode !== 'blank' && !evaluateCondition(instance.includeWhen, scope))
            continue;
        const bucketKey = instance.part ?? declaredParts[0]?.key ?? 'main';
        const bucket = partBuckets.get(bucketKey);
        if (!bucket) {
            diagnostics.push({
                severity: 'error',
                code: 'unknown_document_part',
                message: `Block instance "${instance.instanceId}" targets undeclared part "${bucketKey}".`,
                instanceId: instance.instanceId,
            });
            continue;
        }
        const compiled = compileBlock(definition, scope, context, {
            ...(options.validate === undefined ? {} : { validate: options.validate }),
            instanceId: instance.instanceId,
        });
        diagnostics.push(...compiled.diagnostics);
        attachments.push(...compiled.attachments);
        bucket[instance.slot ?? 'body'].push(...compiled.nodes.map((node) => ({ ...node, instanceId: instance.instanceId })));
    }
    const parts = declaredParts.map((p) => {
        const bucket = partBuckets.get(p.key);
        return {
            key: p.key,
            title: p.title,
            body: bucket?.body ?? [],
            ...(bucket?.letterhead.length ? { letterhead: bucket.letterhead } : {}),
            ...(bucket?.footer.length ? { footer: bucket.footer } : {}),
        };
    });
    const seenAttachments = new Set();
    const dedupedAttachments = attachments.filter((a) => {
        if (seenAttachments.has(a.id))
            return false;
        seenAttachments.add(a.id);
        return true;
    });
    return {
        diagnostics,
        document: {
            schema: IR_SCHEMA,
            version: IR_VERSION,
            locale: composition.locale ?? context.locale,
            title: composition.title,
            ...(composition.meta?.numberStyle ? { numberStyle: composition.meta.numberStyle } : {}),
            parts,
            attachments: dedupedAttachments,
            manifest,
        },
    };
}
/**
 * Compile + assemble in one step — the normal entry point.
 *
 * Numbering style rides on the composition's metadata onto the document, so
 * nobody has to remember that a förhandlingsordning prints "4." where a
 * framställan prints "§ 4".
 */
export function buildDocument(composition, registry, context, options = {}) {
    const compiled = compileComposition(composition, registry, context, options);
    const assembled = assembleDocument(compiled.document, options);
    const diagnostics = [...compiled.diagnostics, ...assembled.diagnostics];
    return {
        document: assembled.document,
        diagnostics,
        ok: !diagnostics.some((d) => d.severity === 'error'),
    };
}
//# sourceMappingURL=compose.js.map